import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { API_BASE } from '../lib/apiBase';
import { Download, Upload, CheckCircle, FileText, Camera, PenLine, Info } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { isMobileDevice } from '../lib/device';
import PhotoCorrection from '../components/PhotoCorrection';

interface SigningData {
  signing: {
    id: number;
    status: 'pending' | 'completed';
    customerEmail: string;
    createdAt: string;
    bitteKontoauszug?: boolean;
  };
  files: Array<{
    id: number;
    originalName: string;
    size: number;
  }>;
  documents: Array<{ type: string; label: string; name: string }>;
  vorgang?: { hasKontoauszug: boolean };
  isVn?: boolean;
  /** VN-Runde: Unterschriftsseiten (1-basiert) — je Seite ein Foto. */
  vnPages?: number[];
}

interface UploadFileResult {
  name: string;
  accepted: boolean;
  detectedCategory: string | null;
  kind: 'signed' | 'kontoauszug' | 'rechnung' | 'sonstiges';
  warning?: string;
}
interface UploadResponse {
  completed: boolean;
  signed: boolean;
  confidence: number;
  /** Erklärt, welche erwarteten Dokumenttypen noch fehlen (siehe signingIsComplete im Backend) —
   *  bisher vom Backend geliefert, aber nirgends angezeigt (Kundenfeedback: keine klare
   *  Erfolgs-/Fehlermeldung nach dem Hochladen). */
  warning?: string;
  missingTypes?: string[];
  files?: UploadFileResult[];
}

export default function SigningPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<SigningData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  // Gerät erkennen: Handy → Upload/Kamera direkt; PC → QR „weiter am Handy".
  const [isMobile] = useState(() => isMobileDevice());
  // Desktop-Assistent: ein Schritt nach dem anderen statt alles auf einmal (Kundenfeedback
  // „zu viel Infos auf einmal"). Mobile bleibt unverändert — dort ist die Kamera-Checkliste
  // schon schlank genug.
  const [desktopStep, setDesktopStep] = useState<'download' | 'sign' | 'upload' | 'kontoauszug'>('download');
  // Dritter Schritt „Kontoauszug": erst die Ja/Nein-Frage, bei „Ja" dann die Ablagefläche.
  const [kontoJa, setKontoJa] = useState(false);
  // Zwei Buttons „Kamera" / „PDF hochladen" statt den QR-Code sofort zu zeigen — erst nach
  // Klick auf „Kamera" erscheint er, und die Seite scrollt dorthin.
  const [showQr, setShowQr] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);
  // „PDF hochladen"-Button öffnet direkt den Datei-Dialog — keine Ablagefläche dazwischen.
  const pdfInputRef = useRef<HTMLInputElement>(null);
  // Dasselbe für den Kontoauszug-Schritt — eigener Ref, weil er ueber pickManualFile()
  // laeuft (erkennt Bild vs. PDF), nicht ueber den generischen stagedFiles-Weg.
  const kontoPdfInputRef = useRef<HTMLInputElement>(null);
  // Nach „Kamera (Handy)": der PC-Tab weiss nicht automatisch, wann das Handy fertig ist —
  // er fragt periodisch nach (siehe Polling-Effekt unten). Sobald sich was am Datei-Stand
  // aendert, zeigt er statt automatisch weiterzuspringen erst eine kurze Bestaetigung mit
  // „Weiter" — der Kunde sieht so selbst, dass der Upload angekommen ist.
  const [phoneUploadSeen, setPhoneUploadSeen] = useState<SigningData['files'] | null>(null);
  // Der QR-Code (siehe unten) hängt ?qr=1 an die URL an. Nur wer darüber vom PC aufs
  // Handy wechselt, hat Schritt 1 (Download/Ausdrucken) schon erledigt — wer den Link
  // direkt aus der Mail auf dem Handy öffnet, braucht ihn weiterhin.
  const viaQr = searchParams.get('qr') === '1';
  const hideDownloadStep = isMobile && viaQr;
  // Wird in dieser Runde ein Kontoauszug gebraucht? Rein danach, ob explizit erbeten —
  // ob im Vorgang schon einer liegt, entscheidet NICHT mit: wer trotzdem den Haken setzt
  // (z.B. weil der vorhandene veraltet ist), will auch wirklich einen neuen anfordern.
  const brauchtKontoauszug = !!data?.signing.bitteKontoauszug;

  // Runde ganz ohne Unterschriftsdokument: es wird allein der Kontoauszug gebraucht.
  // Dann sind Ausdrucken/Unterschreiben/Herunterladen gegenstandslos.
  const nurKontoauszug = !!data && (data.documents?.length ?? 0) === 0
    && !!data.signing.bitteKontoauszug;

  // Desktop: eigener dritter Schritt nur für den Kontoauszug — getrennt vom Hochladen des
  // unterschriebenen Dokuments, statt beides auf einem Bildschirm (Kundenfeedback).
  const showKontoScreen = nurKontoauszug || desktopStep === 'kontoauszug';

  // Was am Handy fotografiert werden soll. Beim Verwendungsnachweis EINE Aufgabe je
  // Unterschriftsseite (der Rücklauf wird positionsgleich zurückgemergt), sonst je Dokument.
  const fotoAufgaben: Array<{ type: string; label: string }> = data?.isVn
    ? (data.vnPages ?? []).map(p => ({ type: `vn:${p}`, label: `Seite ${p}` }))
    : (data?.documents ?? []).map(d => ({ type: d.type, label: d.label }));

  // Der Kontoauszug wird nicht unterschrieben, gehört am Handy aber in dieselbe Liste —
  // sonst gäbe es dafür keinen Knopf, sondern nur eine Ablagefläche weiter unten.
  // Ausnahme: Der QR-Code AUS DEM KONTOAUSZUG-SCHRITT hängt „only=kontoauszug" an — zu
  // diesem Zeitpunkt ist das Hauptdokument schon hochgeladen, die Checkliste soll dann
  // NICHT nochmal danach fragen.
  const onlyKontoauszug = searchParams.get('only') === 'kontoauszug';
  const handyAufgaben = onlyKontoauszug
    ? [{ type: 'kontoauszug', label: 'Kontoauszug' }]
    : brauchtKontoauszug
      ? [...fotoAufgaben, { type: 'kontoauszug', label: 'Kontoauszug' }]
      : fotoAufgaben;
  const [qrCodeUrl] = useState(() => {
    if (typeof window === 'undefined') return '';
    const url = new URL(window.location.href);
    url.searchParams.set('qr', '1');
    return url.toString();
  });
  // Eigener QR-Link fuer den Kontoauszug-Schritt (siehe onlyKontoauszug oben).
  const [qrCodeUrlKonto] = useState(() => {
    if (typeof window === 'undefined') return '';
    const url = new URL(window.location.href);
    url.searchParams.set('qr', '1');
    url.searchParams.set('only', 'kontoauszug');
    return url.toString();
  });
  // Letztes Upload-Ergebnis (KI-Erkennung pro Datei) für Badges + Mismatch-Dialog.
  const [lastResult, setLastResult] = useState<UploadResponse | null>(null);
  // Sanfter Hinweis vor Abschluss, wenn kein Kontoauszug dabei war.
  const [missingKontoConfirm, setMissingKontoConfirm] = useState(false);
  // Pflicht-Bestätigung „handschriftlich unterschrieben" (BAFA erkennt digitale
  // Unterschriften nicht an). Sperrt das Absenden; bleibt über mehrere Uploads gesetzt.
  const [handConfirmed, setHandConfirmed] = useState(false);
  // Inkrement 2: je Dokumenttyp EIN abfotografiertes Unterschriftsseiten-Foto.
  const [photos, setPhotos] = useState<Record<string, File>>({});
  // Backend-400 beim Foto-Upload (z.B. HEIC) inline im Foto-Block zeigen, statt
  // den Vollbild-Fehler auszulösen (siehe error-State + Early-Return unten).
  const [photoError, setPhotoError] = useState<string | null>(null);
  // Dasselbe für den PDF-Weg: Ein abgelehnter Upload (z.B. „bereits hochgeladen") ist ein
  // Hinweis zum Weiterarbeiten, kein Totalausfall. Früher landete er im error-State und der
  // Kunde sah nur „Upload fehlgeschlagen / Bitte wenden Sie sich an Ihren Berater" — ohne zu
  // erfahren, was zu tun ist. Der Vollbild-Fehler bleibt dem ungültigen Link vorbehalten.
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Inkrement 3: Foto läuft erst durch Zuschnitt/Perspektivkorrektur (PhotoCorrection),
  // bevor es in `photos[type]` landet. correctingType===null → normale Checkliste.
  const [correctingType, setCorrectingType] = useState<string | null>(null);
  const [correctingFile, setCorrectingFile] = useState<File | null>(null);
  // Manueller Upload (Icon-Button neben dem Foto-Button): Dialog mit Handschrift-Hinweis,
  // erlaubt Bild ODER PDF. Bilder laufen in denselben Zuschnitt-Flow wie Kamera-Fotos,
  // PDFs landen in stagedFiles und gehen über den normalen /upload-Weg raus (upload-photos
  // konvertiert Bilder → PDF und würde an einer echten PDF scheitern).
  const [uploadDialogType, setUploadDialogType] = useState<string | null>(null);

  const capturePhoto = (type: string, list: FileList | null) => {
    const file = list?.[0];
    if (!file) return;
    setPhotoError(null);
    setCorrectingType(type);
    setCorrectingFile(file);
  };

  const pickManualFile = (type: string, list: FileList | null) => {
    const file = list?.[0];
    setUploadDialogType(null);
    if (!file) return;
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (isPdf) {
      setStagedFiles(prev => [...prev, file]);
      setPhotoError(null);
      return;
    }
    capturePhoto(type, list);
  };

  const handleCorrectionConfirm = (blob: Blob) => {
    if (!correctingType) return;
    const file = new File([blob], `${correctingType}.jpg`, { type: 'image/jpeg' });
    setPhotos(prev => ({ ...prev, [correctingType]: file }));
    setCorrectingType(null);
    setCorrectingFile(null);
  };

  const handleCorrectionRetake = () => {
    setCorrectingType(null);
    setCorrectingFile(null);
  };

  const uploadPhotos = async () => {
    const entries = Object.entries(photos);
    // Ohne Unterschriftsdokument (reine Kontoauszug-Runde) gibt es nichts zu bestätigen.
    if (!token || entries.length === 0 || (!handConfirmed && !nurKontoauszug)) return;
    setUploading(true);
    setPhotoError(null);
    const formData = new FormData();
    const types: string[] = [];
    entries.forEach(([type, file]) => { formData.append('photos', file); types.push(type); });
    formData.append('photoTypes', JSON.stringify(types));
    formData.append('handConfirmed', '1');
    try {
      const res = await fetch(`${API_BASE}/api/signing/${token}/upload-photos`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setPhotoError(body?.message || 'Upload fehlgeschlagen. Bitte erneut versuchen.');
        return;
      }
      const result: UploadResponse = await res.json().catch(() => ({ completed: true, signed: false, confidence: 0 }));
      setLastResult(result);
      // Eine nicht erkannte Unterschrift stoppt den Kunden nicht mehr — die Datei wird
      // trotzdem angenommen und intern zur Prüfung markiert (needsReview, siehe Backend).
      if (result.completed) setUploadDone(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (showQr) qrRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [showQr]);

  // Solange der QR-Code steht, fragt der PC-Tab alle paar Sekunden nach, ob sich am
  // Datei-Stand etwas getan hat (das Handy meldet sich nicht von sich aus). Sobald ja,
  // zeigt phoneUploadSeen eine Bestaetigung statt automatisch weiterzuspringen.
  useEffect(() => {
    if (!showQr || !token) return;
    const startCount = data?.files.length ?? 0;
    const iv = setInterval(() => {
      fetch(`${API_BASE}/api/signing/${token}`)
        .then(r => (r.ok ? r.json() : null))
        .then((fresh: SigningData | null) => {
          if (!fresh) return;
          if (fresh.files.length > startCount || fresh.signing.status === 'completed') {
            clearInterval(iv);
            setData(fresh);
            setShowQr(false);
            setPhoneUploadSeen(fresh.files);
          }
        })
        .catch(() => {});
    }, 3000);
    return () => clearInterval(iv);
  }, [showQr, token]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/signing/${token}`)
      .then(r => {
        if (!r.ok) throw new Error('Link ungültig oder abgelaufen');
        return r.json();
      })
      .then(setData)
      .catch(e => setError(e.message));
  }, [token]);

  const handleDownload = (type: 'all' | 'signature-pages') => {
    window.location.href = `${API_BASE}/api/signing/${token}/download/${type}`;
  };

  // Nach der Bestaetigung „vom Handy hochgeladen" geht's weiter — wohin, haengt davon ab,
  // ob wir schon IM Kontoauszug-Schritt waren (dann fertig) oder gerade erst das
  // Hauptdokument fertig wurde (dann ggf. noch zum Kontoauszug-Schritt).
  const onPhoneUploadConfirmed = () => {
    setPhoneUploadSeen(null);
    if (desktopStep === 'kontoauszug' || !brauchtKontoauszug) setUploadDone(true);
    else setDesktopStep('kontoauszug');
  };

  // „Nein, Kontoauszug gerade nicht zur Hand" — keine Mahnung, nur derselbe Link nochmal
  // per Mail, damit er nicht verloren geht.
  const [kontoReminderSent, setKontoReminderSent] = useState(false);
  const remindKontoPerMail = async () => {
    if (!token) return;
    try {
      await fetch(`${API_BASE}/api/signing/${token}/konto-link-erinnerung`, { method: 'POST' });
    } catch {
      // Mail-Versand kann scheitern (Netz, Postfach) — der Kunde bekommt trotzdem die
      // Bestaetigung, dass seine Antwort angekommen ist; der Berater sieht es im Log.
    }
    setKontoReminderSent(true);
  };

  const uploadFiles = async (files: File[]) => {
    // Ohne handschriftlich-Bestätigung NICHT hochladen. Die Absenden-Buttons sind
    // zwar schon gesperrt (disabled), aber dieser interne Schutz macht die Funktion
    // selbst sicher: kein künftiger Aufrufer kann versehentlich handConfirmed=1
    // schicken, ohne dass der Kunde wirklich bestätigt hat.
    if (!token || files.length === 0 || (!handConfirmed && !nurKontoauszug)) return;
    setUploading(true);
    setUploadError(null);
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    formData.append('handConfirmed', '1');
    try {
      const res = await fetch(`${API_BASE}/api/signing/${token}/upload`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        // Begründung des Servers zeigen (sagt dem Kunden, was zu tun ist); nur wenn keine
        // kommt, bleibt der allgemeine Satz.
        const body = await res.json().catch(() => null);
        setUploadError(body?.message || 'Upload fehlgeschlagen. Bitte erneut versuchen.');
        return;
      }
      const result: UploadResponse = await res.json().catch(() => ({ completed: true, signed: false, confidence: 0 }));
      setLastResult(result);
      const expectKonto = !!data?.signing.bitteKontoauszug;
      const gotKonto = (result.files ?? []).some(f => f.accepted && f.kind === 'kontoauszug');
      // Staged-Files leeren nach jedem angenommenen Upload (eine fehlende Unterschrift
      // stoppt den Kunden nicht mehr, siehe Backend), damit er ggf. weitere Dateien
      // (z.B. Kontoauszug) sauber nachreichen kann.
      if (result.completed || result.signed || gotKonto) setStagedFiles([]);

      if (result.completed) {
        setUploadDone(true);
      } else if (expectKonto && !gotKonto) {
        // Hauptdokument angenommen, aber Kontoauszug fehlt → am Desktop eigener dritter
        // Schritt, am Handy weiterhin der sanfte Hinweis-Dialog.
        if (isMobile) setMissingKontoConfirm(true);
        else setDesktopStep('kontoauszug');
      }
    } catch {
      // Netz-/Verbindungsabbruch: ebenfalls inline, die Dateien bleiben ausgewählt und der
      // Kunde kann direkt erneut absenden.
      setUploadError('Die Verbindung wurde unterbrochen. Bitte senden Sie die Dokumente erneut ab.');
    } finally {
      setUploading(false);
    }
  };

  const addFiles = (list: FileList | null) => {
    if (!list?.length) return;
    const pdfs = Array.from(list).filter(f => f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
    if (pdfs.length) { setStagedFiles(prev => [...prev, ...pdfs]); setUploadError(null); }
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files);
    e.target.value = ''; // erlaubt erneutes Auswählen derselben Datei
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };

  const removeStaged = (idx: number) =>
    setStagedFiles(prev => prev.filter((_, i) => i !== idx));

  const handleConfirm = async () => {
    if (stagedFiles.length === 0) return;
    await uploadFiles(stagedFiles);
  };

  const handleClearStaged = () => {
    setStagedFiles([]);
    setUploadError(null);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
          <p className="text-red-600 font-medium">{error}</p>
          <p className="text-slate-500 text-sm mt-2">Bitte wenden Sie sich an Ihren Berater.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-12 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-7 h-7 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Dokumente zur Unterschrift</h1>
          <p className="text-slate-500 mt-2">
            Bitte laden Sie die Dokumente herunter, unterschreiben Sie diese und laden Sie sie anschließend hoch.
          </p>
        </div>

        {/* Desktop-Assistent: Schrittanzeige, 2 oder 3 Schritte je nachdem ob ein Kontoauszug
            gebraucht wird. Mobile hat keinen Bedarf (Kamera-Checkliste ist schon kurz). */}
        {!isMobile && !nurKontoauszug && (() => {
          const order: Array<typeof desktopStep> = brauchtKontoauszug
            ? ['download', 'sign', 'upload', 'kontoauszug']
            : ['download', 'sign', 'upload'];
          const labels: Record<typeof desktopStep, string> = {
            download: 'Herunterladen', sign: 'Unterschreiben', upload: 'Hochladen', kontoauszug: 'Kontoauszug',
          };
          const currentIdx = order.indexOf(desktopStep);
          return (
            <div className="flex items-center justify-center gap-2 mb-6">
              {order.map((s, i) => {
                const active = s === desktopStep;
                const done = i < currentIdx;
                return (
                  <div key={s} className="flex items-center gap-2">
                    <div className={`flex items-center gap-1.5 text-xs font-medium ${active ? 'text-blue-600' : done ? 'text-slate-500' : 'text-slate-300'}`}>
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${active ? 'bg-blue-600 text-white' : done ? 'bg-slate-400 text-white' : 'bg-slate-200 text-slate-400'}`}>
                        {done ? '✓' : i + 1}
                      </span>
                      {labels[s]}
                    </div>
                    {i < order.length - 1 && <div className={`w-6 h-px ${done ? 'bg-slate-400' : 'bg-slate-200'}`} />}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Anleitung: richtig unterschreiben — eigener Schritt VOR dem Hochladen (Kundenwunsch:
            Schritt „Unterschreiben & Hochladen" in zwei Klicks aufteilen statt ein Bildschirm).
            Bei einer reinen Kontoauszug-Anfrage gibt es nichts zu unterschreiben. Am Handy wie
            bisher immer vor der Kamera-Checkliste (dort kein eigener Schritt noetig). */}
        {!nurKontoauszug && (isMobile || desktopStep === 'sign') && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-white shadow-sm p-6">
          {!isMobile && (
            <button
              onClick={() => setDesktopStep('download')}
              className="text-sm text-slate-400 hover:text-slate-600 mb-4"
            >
              ← Zurück
            </button>
          )}
          <h2 className="text-xl font-bold text-slate-900">Dokumente unterschreiben</h2>
          <p className="text-slate-500 text-sm mt-1 mb-5">So bereiten Sie Ihre Dokumente für den Upload vor.</p>
          <ol className="space-y-3 mb-5">
            <li className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold flex-shrink-0">1</span>
              <span className="text-slate-700">Dokumente ausdrucken</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold flex-shrink-0">2</span>
              <span className="text-slate-900 font-semibold">Mit Stift auf Papier unterschreiben</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold flex-shrink-0">3</span>
              <span className="text-slate-700">Fotografieren oder einscannen</span>
            </li>
          </ol>
          <div className="rounded-lg bg-slate-50 border border-slate-100 p-4 flex items-start gap-3 mb-5">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-slate-900 text-sm">Bitte handschriftlich unterschreiben</p>
              <p className="text-slate-500 text-sm mt-0.5">Eine digitale Signatur wird für diesen Vorgang nicht akzeptiert.</p>
            </div>
          </div>
          {!isMobile && (
            <div className="border-t border-slate-100 pt-4">
              <label className="flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={handConfirmed}
                  onChange={e => setHandConfirmed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 flex-shrink-0 accent-blue-600"
                />
                <span className="text-sm text-slate-700">
                  Ich habe die Dokumente mit Stift auf Papier unterschrieben.
                </span>
              </label>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setDesktopStep('upload')}
                  disabled={!handConfirmed}
                  className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm font-semibold"
                >
                  Weiter
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Download: entfällt nur, wenn man per QR-Code vom PC aufs Handy gewechselt ist (Schritt 1 dort schon erledigt). */}
        {!hideDownloadStep && !nurKontoauszug && (isMobile || desktopStep === 'download') && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-4">
            <h2 className="font-semibold text-slate-900 mb-4">Schritt 1: Dokumente herunterladen</h2>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => handleDownload('all')}
                className="flex items-center gap-3 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors text-sm font-medium"
              >
                <Download className="w-5 h-5" />
                <div className="text-left">
                  <div>Alle Dokumente herunterladen</div>
                  <div className="text-slate-400 text-xs font-normal">
                    {data.documents.length === 0
                      ? 'PDF mit allen Dokumenten'
                      : `PDF mit ${data.documents.length} Dokument${data.documents.length === 1 ? '' : 'en'}: ${data.documents.map(d => d.label).join(', ')}`}
                  </div>
                </div>
              </button>
              <button
                onClick={() => handleDownload('signature-pages')}
                className="flex items-center gap-3 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                <Download className="w-5 h-5" />
                <div className="text-left">
                  <div>Nur Unterschriftseiten</div>
                  <div className="text-blue-200 text-xs font-normal">PDF nur mit den Seiten, die unterschrieben werden müssen</div>
                </div>
              </button>
            </div>
            {!isMobile && (
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setDesktopStep('sign')}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  Weiter
                </button>
              </div>
            )}
          </div>
        )}

        {/* Letztes Upload-Ergebnis: KI-Erkennung pro Datei. Nicht im Kontoauszug-Schritt —
            dort geht es um ein ANDERES Dokument, das Ergebnis aus Schritt 3 wäre dort nur
            verwirrender Alt-Stand statt Kontext (Kundenwunsch). */}
        {lastResult?.files && lastResult.files.length > 0 && !uploadDone && desktopStep !== 'kontoauszug' && (
          <div className="mb-4 bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">Erkennung</p>
            <ul className="space-y-1.5 text-sm">
              {lastResult.files.map((f, i) => {
                const ok = f.accepted;
                return (
                  <li key={i} className="flex items-start gap-2">
                    <span className={`mt-0.5 ${ok ? 'text-emerald-600' : 'text-amber-600'}`}>{ok ? '✓' : '⚠'}</span>
                    <span className="flex-1 min-w-0">
                      <span className="font-medium text-slate-800 truncate block">{f.name}</span>
                      <span className="text-xs text-slate-500">
                        {f.kind === 'signed' && 'Unterschriebenes Dokument erkannt'}
                        {f.kind === 'kontoauszug' && 'Erkannt als: Kontoauszug'}
                        {f.kind === 'rechnung' && 'Erkannt als: Rechnung'}
                        {f.kind === 'sonstiges' && (f.warning ?? 'Kein passender Typ erkannt')}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 flex items-center gap-1.5 text-sm text-emerald-700">
              <CheckCircle className="w-4 h-4 flex-shrink-0" /> Diese Dateien wurden übermittelt und gespeichert.
            </p>
            {lastResult.warning && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm text-amber-800">{lastResult.warning}</p>
              </div>
            )}
          </div>
        )}

        {/* Upload — am Desktop in den Schritten „upload" (unterschriebenes Dokument) und
            „kontoauszug" (eigener vierter Schritt) sichtbar; bei reiner Kontoauszug-Anfrage
            gibt es keine vorherigen Schritte, also immer sichtbar. */}
        {(isMobile || nurKontoauszug || desktopStep === 'upload' || desktopStep === 'kontoauszug') && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          {!isMobile && !nurKontoauszug && (desktopStep === 'upload' || desktopStep === 'kontoauszug') && (
            <button
              onClick={() => setDesktopStep(desktopStep === 'kontoauszug' ? 'upload' : 'sign')}
              className="text-sm text-slate-500 hover:text-slate-700 mb-3"
            >
              ← Zurück
            </button>
          )}
          {!isMobile && (nurKontoauszug || desktopStep === 'kontoauszug') ? (
            <>
              <h2 className="text-xl font-bold text-slate-900">Zahlungsnachweis hochladen</h2>
              <p className="text-slate-500 text-sm mt-1 mb-5">
                Laden Sie einen Kontoauszug oder eine Zahlungsbestätigung Ihrer Bank mit Unterschrift und Stempel hoch.
                Der Kontoinhaber und die Zahlung der Beratungsrechnung müssen erkennbar sein.
              </p>
            </>
          ) : (
            <>
              <h2 className="font-semibold text-slate-900 mb-4">
                {nurKontoauszug || desktopStep === 'kontoauszug'
                  ? 'Kontoauszug hochladen'
                  : (!isMobile || hideDownloadStep) ? 'Auf Papier unterschriebene Dokumente hochladen' : 'Schritt 2: Auf Papier unterschriebene Dokumente hochladen'}
              </h2>
              {data.signing.bitteKontoauszug && (isMobile || showKontoScreen) && (
                <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-sm text-emerald-800">
                    {nurKontoauszug
                      ? <>Bitte laden Sie hier Ihren <strong>aktuellen Kontoauszug</strong> hoch, der die Zahlung des Beratungshonorars zeigt. Unterschreiben müssen Sie dafür nichts.</>
                      : <><strong>Erinnerung:</strong> Bitte laden Sie hier zusätzlich Ihren <strong>aktuellen Kontoauszug</strong> mit hoch.</>}
                  </p>
                </div>
              )}
            </>
          )}
          {isMobile && !uploadDone && data.signing.status !== 'completed' && handyAufgaben.length > 0 && (
            <div className="mb-6 flex flex-col gap-3">
              {correctingType && correctingFile ? (
                <PhotoCorrection photoFile={correctingFile} onConfirm={handleCorrectionConfirm} onRetake={handleCorrectionRetake} />
              ) : (
                <>
              <p className="text-sm text-slate-600">
                {data.isVn
                  ? <>Fotografieren Sie die <strong>unterschriebenen Seiten</strong> des Verwendungsnachweises einzeln ab — die Seitenzahl steht auf dem Ausdruck ({Object.keys(photos).length} von {fotoAufgaben.length} fotografiert).</>
                  : fotoAufgaben.length === 0
                    ? <>Fotografieren Sie Ihren <strong>Kontoauszug</strong> ab oder wählen Sie die Datei aus.</>
                    : <>Fotografieren Sie je Dokument die <strong>unterschriebene Seite</strong> ab ({Object.keys(photos).length} von {fotoAufgaben.length} fotografiert).</>}
              </p>
              {handyAufgaben.map(doc => {
                const has = !!photos[doc.type];
                return (
                  <div key={doc.type} className="flex items-stretch gap-2">
                    <label
                      className={`flex-1 min-w-0 flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${has ? 'border-green-300 bg-green-50' : 'border-slate-200 hover:border-blue-400'}`}
                    >
                      {has ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" /> : <Camera className="w-5 h-5 text-blue-600 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900">{doc.label}</div>
                        <div className="text-xs text-slate-500">
                          {has
                            ? 'Foto aufgenommen – tippen zum Ersetzen'
                            : doc.type === 'kontoauszug'
                              ? <>Zahlung der Beratungsrechnung fotografieren</>
                              : <><strong>Handschriftliche</strong> Unterschrift fotografieren</>}
                        </div>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={e => { capturePhoto(doc.type, e.target.files); e.target.value = ''; }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setUploadDialogType(doc.type)}
                      title="Datei manuell hochladen"
                      aria-label={`${doc.label}: Datei manuell hochladen`}
                      className="flex-shrink-0 w-12 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-blue-400 hover:text-blue-600 hover:bg-slate-50 transition-colors"
                    >
                      <Upload className="w-5 h-5" />
                    </button>
                  </div>
                );
              })}
              {Object.keys(photos).length > 0 && (
                <>
                  {photoError && (
                    <div className="rounded-lg border border-red-300 bg-red-50 p-3">
                      <p className="text-sm text-red-700">{photoError}</p>
                    </div>
                  )}
                  {!nurKontoauszug && (
                  <label className="flex items-start gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={handConfirmed}
                      onChange={e => setHandConfirmed(e.target.checked)}
                      className="mt-0.5 h-4 w-4 flex-shrink-0"
                    />
                    <span className="text-sm text-slate-700">
                      Ich habe die Dokumente ausgedruckt und mit Stift auf Papier unterschrieben (keine digitale Signatur).
                      <span className="block text-xs text-slate-500 mt-0.5">Die BAFA erkennt digitale Unterschriften nicht an.</span>
                    </span>
                  </label>
                  )}
                  <button
                    onClick={() => uploadPhotos()}
                    disabled={uploading || (!handConfirmed && fotoAufgaben.length > 0) || (data.isVn && Object.keys(photos).length < fotoAufgaben.length)}
                    title={data.isVn && Object.keys(photos).length < fotoAufgaben.length ? 'Bitte zuerst alle Unterschriftsseiten fotografieren' : undefined}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition-colors text-sm font-medium w-fit"
                  >
                    <Upload className={`w-4 h-4 ${uploading ? 'animate-bounce' : ''}`} />
                    {uploading ? 'Wird übermittelt…' : `${Object.keys(photos).length} Foto${Object.keys(photos).length === 1 ? '' : 's'} übermitteln`}
                  </button>
                </>
              )}
                </>
              )}
            </div>
          )}
          {uploadDone || data.signing.status === 'completed' ? (
            <div className="flex items-center gap-3 text-green-600">
              <CheckCircle className="w-6 h-6" />
              <span className="font-medium">Vielen Dank! Dokumente erfolgreich übermittelt.</span>
            </div>
          ) : !isMobile && stagedFiles.length === 0 ? (
            <div className="flex flex-col items-center gap-4 text-center py-2">
              {phoneUploadSeen ? (
                <>
                  <CheckCircle className="w-10 h-10 text-green-600" />
                  <p className="text-base font-medium text-slate-800">Vom Handy hochgeladen:</p>
                  <ul className="text-sm text-slate-600">
                    {phoneUploadSeen.map(f => <li key={f.id}>{f.originalName}</li>)}
                  </ul>
                  <button
                    onClick={onPhoneUploadConfirmed}
                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                  >
                    Weiter
                  </button>
                </>
              ) : (
                <>
              {!showKontoScreen && fotoAufgaben.length > 0 && (
                !showQr ? (
                  <>
                    <p className="text-base font-medium text-slate-800">Wie möchten Sie das unterschriebene Dokument hochladen?</p>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setShowQr(true)}
                        className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                      >
                        <Camera className="w-4 h-4" /> Kamera (Handy)
                      </button>
                      <button
                        onClick={() => pdfInputRef.current?.click()}
                        className="flex items-center gap-2 px-5 py-3 border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg transition-colors text-sm font-medium"
                      >
                        <Upload className="w-4 h-4" /> PDF hochladen
                      </button>
                      <input
                        ref={pdfInputRef}
                        type="file"
                        accept=".pdf"
                        multiple
                        className="hidden"
                        onChange={handleUpload}
                      />
                    </div>
                  </>
                ) : (
                  <div ref={qrRef}>
                    <div className="bg-white p-3 rounded-lg border border-slate-200 mx-auto w-fit">
                      <QRCodeSVG value={qrCodeUrl} size={176} />
                    </div>
                    <p className="text-base font-medium text-slate-800 mt-4">QR-Code mit der Handy-Kamera scannen und ein <strong>Foto des handschriftlich unterschriebenen Dokuments</strong> hochladen.</p>
                    <button
                      onClick={() => setShowQr(false)}
                      className="text-xs text-slate-400 hover:text-slate-600 underline mt-3"
                    >
                      ← Zurück zur Auswahl
                    </button>
                  </div>
                )
              )}
              {/* Der Kontoauszug wird nicht unterschrieben, aber auch er kann per Kamera (Handy,
                  ueber denselben QR-Mechanismus wie oben) oder PDF (direkt von diesem Geraet)
                  kommen. Bei nurKontoauszug gibt es nichts zu fragen (die Mail bat schon gezielt
                  darum) — sonst erst Ja/Nein. */}
              {showKontoScreen && brauchtKontoauszug && (
                nurKontoauszug || kontoJa ? (
                  !showQr ? (
                    <>
                      <p className="text-base font-medium text-slate-800">Wie möchten Sie den Kontoauszug hochladen?</p>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setShowQr(true)}
                          className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                        >
                          <Camera className="w-4 h-4" /> Kamera (Handy)
                        </button>
                        <button
                          onClick={() => kontoPdfInputRef.current?.click()}
                          className="flex items-center gap-2 px-5 py-3 border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg transition-colors text-sm font-medium"
                        >
                          <Upload className="w-4 h-4" /> PDF hochladen
                        </button>
                        <input
                          ref={kontoPdfInputRef}
                          type="file"
                          accept="image/*,application/pdf,.pdf"
                          className="hidden"
                          onChange={e => { pickManualFile('kontoauszug', e.target.files); e.target.value = ''; }}
                        />
                      </div>
                    </>
                  ) : (
                    <div ref={qrRef}>
                      <div className="bg-white p-3 rounded-lg border border-slate-200 mx-auto w-fit">
                        <QRCodeSVG value={qrCodeUrlKonto} size={176} />
                      </div>
                      <p className="text-base font-medium text-slate-800 mt-4">QR-Code mit der Handy-Kamera scannen und den <strong>Kontoauszug</strong> fotografieren.</p>
                      <button
                        onClick={() => setShowQr(false)}
                        className="text-xs text-slate-400 hover:text-slate-600 underline mt-3"
                      >
                        ← Zurück zur Auswahl
                      </button>
                    </div>
                  )
                ) : kontoReminderSent ? (
                  <>
                    <CheckCircle className="w-10 h-10 text-green-600" />
                    <p className="text-base font-medium text-slate-800">Mail verschickt — Sie finden den Link darin, sobald Sie den Kontoauszug zur Hand haben.</p>
                  </>
                ) : (
                  <>
                    <p className="text-base font-medium text-slate-800">Haben Sie Ihren aktuellen Kontoauszug schon zur Hand?</p>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setKontoJa(true)}
                        className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                      >
                        Ja
                      </button>
                      <button
                        onClick={remindKontoPerMail}
                        className="px-5 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                      >
                        Nein — später per Mail nachreichen
                      </button>
                    </div>
                  </>
                )
              )}
                </>
              )}
            </div>
          ) : isMobile && handyAufgaben.length > 0 && stagedFiles.length === 0 ? null : stagedFiles.length > 0 ? (
            <div className="flex flex-col gap-4">
              <ul className="flex flex-col gap-2">
                {stagedFiles.map((f, i) => (
                  <li key={i} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-900 truncate flex-1">{f.name}</span>
                    {!uploading && (
                      <button onClick={() => removeStaged(i)} title="Entfernen" className="text-slate-400 hover:text-red-600 text-sm flex-shrink-0">✕</button>
                    )}
                  </li>
                ))}
              </ul>
              <label className="text-sm text-blue-600 hover:text-blue-700 underline cursor-pointer w-fit">
                Weitere Dateien hinzufügen
                <input type="file" accept=".pdf" multiple className="hidden" onChange={handleUpload} />
              </label>
              {uploadError && (
                <div className="rounded-lg border border-red-300 bg-red-50 p-3">
                  <p className="text-sm font-semibold text-red-800">Upload nicht möglich</p>
                  <p className="text-sm text-red-700 mt-1">{uploadError}</p>
                </div>
              )}
              {/* Am Desktop schon im Schritt „sign" bestätigt — hier nur für Mobile nötig
                  (z.B. wenn dort statt Foto eine PDF manuell hochgeladen wird). */}
              {!nurKontoauszug && isMobile && (
              <label className="flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={handConfirmed}
                  onChange={e => setHandConfirmed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 flex-shrink-0"
                />
                <span className="text-sm text-slate-700">
                  Ich habe die Dokumente ausgedruckt und mit Stift auf Papier unterschrieben (keine digitale Signatur).
                  <span className="block text-xs text-slate-500 mt-0.5">Die BAFA erkennt digitale Unterschriften nicht an.</span>
                </span>
              </label>
              )}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleConfirm}
                  disabled={uploading || (!handConfirmed && !nurKontoauszug)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  <Upload className={`w-4 h-4 ${uploading ? 'animate-bounce' : ''}`} />
                  {uploading ? 'Wird übermittelt…' : `${stagedFiles.length} Dokument${stagedFiles.length === 1 ? '' : 'e'} übermitteln`}
                </button>
                <button
                  onClick={handleClearStaged}
                  disabled={uploading}
                  className="text-sm text-slate-500 hover:text-slate-700 underline disabled:opacity-50"
                >
                  Alle entfernen
                </button>
              </div>
            </div>
          ) : (
            <label
              className="flex flex-col items-center gap-3 border-2 border-dashed border-slate-300 rounded-lg p-6 cursor-pointer hover:border-blue-400 hover:bg-slate-50 transition-colors"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <Upload className="w-8 h-8 text-slate-400" />
              <span className="text-sm text-slate-600">PDFs hier ablegen oder klicken zum Auswählen<br /><span className="text-xs text-slate-500">unterschriebene Dokumente, Kontoauszug, …</span></span>
              <input
                type="file"
                accept=".pdf"
                multiple
                className="hidden"
                onChange={handleUpload}
              />
            </label>
          )}
        </div>
        )}
      </div>

      {uploadDialogType && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setUploadDialogType(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            role="dialog"
            aria-modal="true"
            onClick={e => e.stopPropagation()}
          >
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <PenLine className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Nur handschriftlich unterschriebene Dokumente</h3>
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-base text-red-900">
              <p>
                Bitte laden Sie hier ausschließlich Dokumente hoch, die Sie <strong>ausgedruckt und mit Stift auf
                Papier unterschrieben</strong> haben.
              </p>
              <p className="font-bold mt-2">Eine digitale Signatur akzeptiert die BAFA nicht.</p>
            </div>
            <p className="text-xs text-slate-500 mt-2 mb-5">Möglich sind Bilder (JPG, PNG) oder PDF-Dateien.</p>
            <div className="flex flex-col gap-2">
              <label className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg cursor-pointer">
                <Upload className="w-4 h-4" />
                Bild oder PDF auswählen
                <input
                  type="file"
                  accept="image/*,application/pdf,.pdf"
                  className="hidden"
                  onChange={e => { pickManualFile(uploadDialogType, e.target.files); e.target.value = ''; }}
                />
              </label>
              <button
                onClick={() => setUploadDialogType(null)}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {missingKontoConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="font-semibold text-slate-900 mb-2">Kontoauszug fehlt noch</h3>
            <p className="text-sm text-slate-600 mb-5">
              Wir hatten um Ihren <strong>Kontoauszug</strong> gebeten — in den hochgeladenen Dateien wurde keiner erkannt.
              Möchten Sie ihn jetzt nachreichen oder später per E-Mail-Antwort schicken?
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setMissingKontoConfirm(false)}
                className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
              >
                Jetzt Kontoauszug zusätzlich hochladen
              </button>
              <button
                onClick={() => { setMissingKontoConfirm(false); setUploadDone(true); }}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Später per Mail nachreichen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
